import React from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

export default function BondCantFindDialog({
  open,
  onClose,
  cfSubmitting,
  cfBondNumber,
  setCfBondNumber,
  cfBondType,
  setCfBondType,
  cfName,
  setCfName,
  cfPrincipalName,
  setCfPrincipalName,
  cfAction,
  setCfAction,
  cfOtherInfo,
  setCfOtherInfo,
  bondTypes,
  onSubmit,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => !cfSubmitting && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
        Can&apos;t find a bond?
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Provide details below and our team will review and get back to you.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Bond Number"
            placeholder="e.g., PR-123456"
            value={cfBondNumber}
            onChange={(e) => setCfBondNumber(e.target.value)}
            fullWidth
          />

          <Autocomplete
            options={bondTypes}
            value={cfBondType}
            onChange={(e, v) => setCfBondType(v)}
            getOptionLabel={(o) => o?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            }
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            renderInput={(p) => <TextField {...p} label="Bond Type" />}
          />

          <TextField
            label="Name (optional)"
            placeholder="Estate / Ward / Trust / Beneficiary"
            value={cfName}
            onChange={(e) => setCfName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Principal Name (optional)"
            value={cfPrincipalName}
            onChange={(e) => setCfPrincipalName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Action you want to perform"
            select
            fullWidth
            value={cfAction}
            onChange={(e) => setCfAction(e.target.value)}
            slotProps={{ select: { native: true }}}
          >
            <option value="cancellation">Cancellation</option>
            <option value="endorsement">Endorsement</option>
            <option value="other">Other</option>
          </TextField>

          <TextField
            label="Other Information"
            placeholder="Provide additional details"
            value={cfOtherInfo}
            onChange={(e) => setCfOtherInfo(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            helperText={
              cfAction === "other" ? "Required when action is Other" : "Optional"
            }
            error={cfAction === "other" && !cfOtherInfo.trim()}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={cfSubmitting}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={cfSubmitting || (cfAction === "other" && !cfOtherInfo.trim())}
          onClick={onSubmit}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BondCantFindDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cfSubmitting: PropTypes.bool.isRequired,
  cfBondNumber: PropTypes.string.isRequired,
  setCfBondNumber: PropTypes.func.isRequired,
  cfBondType: PropTypes.object,
  setCfBondType: PropTypes.func.isRequired,
  cfName: PropTypes.string.isRequired,
  setCfName: PropTypes.func.isRequired,
  cfPrincipalName: PropTypes.string.isRequired,
  setCfPrincipalName: PropTypes.func.isRequired,
  cfAction: PropTypes.string.isRequired,
  setCfAction: PropTypes.func.isRequired,
  cfOtherInfo: PropTypes.string.isRequired,
  setCfOtherInfo: PropTypes.func.isRequired,
  bondTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubmit: PropTypes.func.isRequired,
};
